import { useEffect, useState, useCallback } from 'react';
import { getWorkout, getAIFeedback, getRunningStats, getAssetUrl, deleteWorkout, Workout, FeedbackData, RunningStats, WorkoutScore } from '../api/client';
import { LoadingState, ErrorState } from '../components/States';
import { AIFeedback } from '../components/AIFeedback';
import { haptic } from '../lib/telegram';
import { formatLongDate } from '../lib/date';
import './WorkoutDetail.css';

interface WorkoutDetailProps {
  workoutId: number;
  userId: number;
  onBack: () => void;
}

const formatPace = (secPerKm?: number): string => {
  if (!secPerKm) return '—';
  const m = Math.floor(secPerKm / 60);
  const s = secPerKm % 60;
  return `${m}'${String(s).padStart(2, '0')}"/км`;
};

const formatDuration = (seconds?: number): string => {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
};

const WorkoutScoreCard: React.FC<{ score: WorkoutScore }> = ({ score }) => (
  <section className="score-detail">
    <div className="score-ring" style={{ '--score': `${score.total * 3.6}deg` } as React.CSSProperties}>
      <div><strong>{score.total}</strong><span>/100</span></div>
    </div>
    <div className="score-copy">
      <span className="score-kicker">RUN SCORE · {score.level}</span>
      <h2>{score.summary}</h2>
      <p>{score.provisional ? 'Предварительная оценка: заполните профиль и добавьте ещё тренировки.' : 'Персональная оценка по вашему профилю и истории.'}</p>
    </div>
    <div className="score-components">
      {score.components.map(component => (
        <div key={component.key}>
          <span>{component.label}</span>
          <div><i style={{ width: `${(component.score / component.max) * 100}%` }} /></div>
          <strong>{component.score}/{component.max}</strong>
          <p>{component.insight}</p>
        </div>
      ))}
    </div>
  </section>
);

const RunningStatsCard: React.FC<{ stats: RunningStats }> = ({ stats }) => (
  <section className="detail-section">
    <h2 className="section-title">Метрики бега</h2>
    <div className="running-stats-grid">
      {stats.distance_km != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{stats.distance_km} км</div>
          <div className="running-stat-label">Дистанция</div>
        </div>
      )}
      {stats.duration_seconds != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{formatDuration(stats.duration_seconds)}</div>
          <div className="running-stat-label">Время</div>
        </div>
      )}
      {stats.calories != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{stats.calories}</div>
          <div className="running-stat-label">Ккал</div>
        </div>
      )}
      {stats.pace_avg_sec_per_km != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{formatPace(stats.pace_avg_sec_per_km)}</div>
          <div className="running-stat-label">Ср. темп</div>
        </div>
      )}
      {stats.hr_avg != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{stats.hr_avg}</div>
          <div className="running-stat-label">Ср. пульс</div>
        </div>
      )}
      {stats.hr_max != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{stats.hr_max}</div>
          <div className="running-stat-label">Макс. пульс</div>
        </div>
      )}
      {stats.cadence_avg != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{stats.cadence_avg}</div>
          <div className="running-stat-label">Каденс</div>
        </div>
      )}
      {stats.stride_length_cm != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{stats.stride_length_cm} см</div>
          <div className="running-stat-label">Длина шага</div>
        </div>
      )}
      {stats.ground_contact_avg_ms != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{stats.ground_contact_avg_ms} мс</div>
          <div className="running-stat-label">Контакт с землёй</div>
        </div>
      )}
      {stats.vertical_osc_avg_cm != null && (
        <div className="running-stat-card">
          <div className="running-stat-value">{stats.vertical_osc_avg_cm} см</div>
          <div className="running-stat-label">Верт. колебания</div>
        </div>
      )}
    </div>

    {(stats.balance_left_pct != null || stats.balance_right_pct != null) && (
      <div className="balance-section">
        <h3 className="subsection-title">Баланс</h3>
        <div className="balance-bar">
          <div className="balance-left" style={{ width: `${stats.balance_left_pct ?? 50}%` }}>
            Л {stats.balance_left_pct ?? 50}%
          </div>
          <div className="balance-right" style={{ width: `${stats.balance_right_pct ?? 50}%` }}>
            П {stats.balance_right_pct ?? 50}%
          </div>
        </div>
      </div>
    )}

    {stats.zones_json && Object.keys(stats.zones_json).length > 0 && (
      <div className="zones-section">
        <h3 className="subsection-title">Зоны нагрузки</h3>
        <div className="zones-list">
          {Object.entries(stats.zones_json).map(([zone, minutes]) => (
            <div key={zone} className="zone-row">
              <span className="zone-name">
                {zone === 'extreme' ? 'Экстрим' :
                 zone === 'anaerobic' ? 'Анаэробная' :
                 zone === 'aerobic' ? 'Аэробная' :
                 zone === 'oxidative' ? 'Окислительная' :
                 zone === 'warmup' ? 'Разминка' : zone}
              </span>
              <span className="zone-value">{minutes} мин</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {stats.recovery_time_hours != null && (
      <div className="recovery-section">
        <h3 className="subsection-title">Восстановление</h3>
        <p className="section-text">
          Время на восстановление: <strong>{stats.recovery_time_hours} ч</strong>
          {stats.training_stress_aerobic != null && <> · Аэробная нагрузка: <strong>{stats.training_stress_aerobic}</strong></>}
          {stats.training_stress_anaerobic != null && <> · Анаэробная: <strong>{stats.training_stress_anaerobic}</strong></>}
        </p>
      </div>
    )}

    {stats.ground_contact_rating && (
      <p className="section-text">
        Оценка контакта с землёй: <strong>{stats.ground_contact_rating}</strong>
      </p>
    )}
  </section>
);

export const WorkoutDetail: React.FC<WorkoutDetailProps> = ({ workoutId, userId, onBack }) => {
  const [workout, setWorkout] = useState<Workout | null>(null);
  const [runningStats, setRunningStats] = useState<RunningStats | null>(null);
  const [feedback, setFeedback] = useState<FeedbackData | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [w, rs] = await Promise.all([
        getWorkout(workoutId),
        getRunningStats(workoutId)
      ]);
      setWorkout(w);
      setRunningStats(rs);
    } catch (err: any) {
      setError(err?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [workoutId]);

  useEffect(() => {
    if (workoutId) load();
  }, [workoutId, load]);

  const handleGenerateFeedback = async () => {
    setGenerating(true);
    try {
      const f = await getAIFeedback(userId, workoutId);
      setFeedback(f);
      haptic.success();
    } catch (err: any) {
      haptic.error();
      setError(err?.message || 'Не удалось получить обратную связь');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить тренировку и исходный скриншот без возможности восстановления?')) return;
    setDeleting(true);
    try {
      await deleteWorkout(userId, workoutId);
      haptic.success();
      onBack();
    } catch (err: any) {
      setError(err?.message || 'Не удалось удалить тренировку');
      haptic.error();
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="workout-detail-page">
        <header className="page-header">
          <button className="back-button" onClick={() => { haptic.medium(); onBack(); }}>←</button>
          <h1>Тренировка</h1>
        </header>
        <LoadingState />
      </div>
    );
  }

  if (error || !workout) {
    return (
      <div className="workout-detail-page">
        <header className="page-header">
          <button className="back-button" onClick={() => { haptic.medium(); onBack(); }}>←</button>
          <h1>Тренировка</h1>
        </header>
        <ErrorState message={error || 'Тренировка не найдена'} onRetry={load} />
      </div>
    );
  }

  const analysis = workout.ai_analysis;
  const isRunningWorkout = workout.source === 'huawei_health' || runningStats != null;

  return (
    <div className="workout-detail-page">
      <header className="page-header">
        <button className="back-button" onClick={() => { haptic.medium(); onBack(); }}>←</button>
        <h1>{formatLongDate(workout.date)}</h1>
      </header>

      {workout.score && <WorkoutScoreCard score={workout.score} />}

      {isRunningWorkout && runningStats && (
        <RunningStatsCard stats={runningStats} />
      )}

      {analysis?.summary && (
        <section className="detail-section">
          <h2 className="section-title">Анализ</h2>
          <p className="section-text">{analysis.summary}</p>
          <div className="detail-chips">
            {analysis.duration && <span className="chip">{analysis.duration}</span>}
            {analysis.totalVolume && <span className="chip">{analysis.totalVolume}</span>}
            {analysis.intensity && <span className="chip">{analysis.intensity}</span>}
          </div>
        </section>
      )}

      {!isRunningWorkout && (
        <section className="detail-section">
          <h2 className="section-title">Упражнения ({workout.exercises.length})</h2>
          {workout.exercises.length === 0 ? (
            <p className="section-text muted">Упражнения не распознаны</p>
          ) : (
            <div className="exercises-full-list">
              {workout.exercises.map((ex, idx) => (
                <div key={ex.id ?? idx} className="exercise-full-card">
                  <div className="exercise-full-header">
                    <span className="exercise-full-index">{idx + 1}</span>
                    <span className="exercise-full-name">{ex.name}</span>
                  </div>
                  <div className="exercise-full-meta">
                    {ex.sets != null && <span className="meta-pill">подходов: {ex.sets}</span>}
                    {ex.reps && <span className="meta-pill">повт: {ex.reps}</span>}
                    {ex.weight && <span className="meta-pill">вес: {ex.weight}</span>}
                  </div>
                  {ex.notes && <p className="exercise-full-notes">{ex.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="detail-section">
        <div className="section-title-row">
          <h2 className="section-title">AI обратная связь</h2>
          {!feedback && (
            <button
              className="generate-feedback-btn"
              onClick={handleGenerateFeedback}
              disabled={generating}
            >
              {generating ? 'Генерация…' : 'Получить'}
            </button>
          )}
        </div>
        {feedback ? (
          <AIFeedback feedback={feedback} />
        ) : (
          <p className="section-text muted">
            Нажмите «Получить», чтобы AI проанализировал эту тренировку и дал рекомендации
          </p>
        )}
      </section>

      {workout.screenshot_url && (
        <section className="detail-section">
          <h2 className="section-title">Исходный отчёт</h2>
          <img
            src={getAssetUrl(workout.screenshot_url)}
            alt="Скриншот тренировки"
            className="screenshot-preview"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </section>
      )}

      <button className="delete-workout-button" onClick={handleDelete} disabled={deleting}>
        {deleting ? 'Удаляем…' : 'Удалить тренировку и скриншот'}
      </button>
    </div>
  );
};
